import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useFocusTrap } from "../../hooks/useFocusTrap";

interface SheetContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue | undefined>(undefined);

function useSheetContext() {
  const context = React.useContext(SheetContext);
  if (!context) {
    throw new Error("Sheet components must be used within a Sheet");
  }
  return context;
}

interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const Sheet = ({ children, open: controlledOpen, onOpenChange }: SheetProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  return (
    <SheetContext.Provider value={{ open, setOpen }}>
      {children}
    </SheetContext.Provider>
  );
};

interface SheetTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const SheetTrigger = React.forwardRef<HTMLButtonElement, SheetTriggerProps>(
  ({ children, asChild, onClick, ...props }, ref) => {
    const { setOpen } = useSheetContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      setOpen(true);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<any>, {
        onClick: handleClick,
      });
    }

    return (
      <button ref={ref} onClick={handleClick} {...props}>
        {children}
      </button>
    );
  }
);
SheetTrigger.displayName = "SheetTrigger";

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "right" | "bottom" | "left";
}

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, children, side = "right", ...props }, ref) => {
    const { open, setOpen } = useSheetContext();
    const focusTrapRef = useFocusTrap(open, () => setOpen(false));

    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        // Set the forwarded ref
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
        // Set the focus trap ref
        (focusTrapRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref, focusTrapRef]
    );

    // While the sheet is open the page behind it must not scroll. Locking only
    // <body> did nothing: this document scrolls on the root element, so the
    // page kept moving under the open menu. Both elements are locked, and the
    // previous values restored (rather than cleared) so an outer scroll lock
    // stays intact if one was already active.
    React.useEffect(() => {
      if (!open) return undefined;
      const root = document.documentElement;
      const previousBody = document.body.style.overflow;
      const previousRoot = root.style.overflow;
      document.body.style.overflow = "hidden";
      root.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousBody;
        root.style.overflow = previousRoot;
      };
    }, [open]);

    const sideClasses = {
      top: "inset-x-0 top-0 border-b",
      right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
      bottom: "inset-x-0 bottom-0 border-t",
      left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
    };

    const translateClasses = {
      top: open ? "translate-y-0" : "-translate-y-full",
      right: open ? "translate-x-0" : "translate-x-full",
      bottom: open ? "translate-y-0" : "translate-y-full",
      left: open ? "translate-x-0" : "-translate-x-full",
    };

    if (!open) return null;

    // Portalled to the document body. A `position: fixed` box is positioned
    // against the viewport ONLY while no ancestor establishes a containing
    // block — and backdrop-filter does establish one. Rendered in place, a
    // sheet opened from inside the landing navbar (`sticky … backdrop-blur-md`)
    // resolved its `inset-0` against that 64px-tall bar instead of the
    // viewport: overlay and panel collapsed to a strip across the header, the
    // menu links spilled out below with no background behind them, and the
    // page showed straight through them. The portal is what keeps a sheet a
    // viewport-level layer no matter which blurred or transformed subtree
    // renders it.
    const sheet = (
      <div role="dialog" aria-modal="true">
        {/* Overlay */}
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        {/* Sheet — one layer above its own overlay so stacking never depends
            on DOM order. */}
        <div
          ref={mergedRef}
          className={cn(
            "fixed z-[60] gap-4 bg-background p-6 shadow-lg transition-transform duration-300 ease-in-out",
            sideClasses[side],
            translateClasses[side],
            className
          )}
          {...props}
        >
          {children}
          <button
            className="absolute right-4 top-4 rounded-full opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      </div>
    );

    return createPortal(sheet, document.body);
  }
);
SheetContent.displayName = "SheetContent";

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
